import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin, Clock, Phone, Mail, Facebook, Instagram, Twitter, // Contact/Footer Icons
} from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useSettingsContext } from '../../../shared/context/SettingsContext';

// --- Data Structures ---
interface FAQItem {
    question: string;
    answer: string;
}

const baseFaqData: FAQItem[] = [
    {
        question: "How do I enroll my child in your programs?",
        answer: "Enrollment is a simple four-step process: inquiry, assessment, consultation, and final enrollment. You can start by contacting us through phone, email, or by filling out the contact form on our website.",
    },
    {
        question: "Do you offer trial classes?",
        answer: "Yes, we offer trial classes to allow students to experience our teaching methods and environment before committing to a full program. Please contact us to schedule a trial class.",
    },
    {
        question: "What curricula do you support?",
        answer: "We support various curricula including CBSE, IGCSE, IB, Federal Board, and Bangladesh Board. Our teachers are experienced in these educational systems and can provide tailored support.",
    },
    {
        question: "How are classes structured?",
        answer: "Classes are typically structured with a mix of instruction, practice, and assessment. We focus on creating an interactive learning environment with personalized attention to each student's needs.",
    },
];

// --- Sub-Components ---

const ContactForm: React.FC = () => {
    const [status, setStatus] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // --- Simulated Form Submission Logic ---
        const formData = new FormData(e.currentTarget as HTMLFormElement);
        const formValues = Object.fromEntries(formData.entries());
        console.log('Form submitted with values:', formValues);
        
        // Simulate API call success
        setTimeout(() => {
            setStatus('success');
            (e.currentTarget as HTMLFormElement).reset();
        }, 1000);
        
        // In a real application, you would make a fetch or axios call here
    };

    return (
        <div className="bg-white rounded-xl shadow-lg text-foreground shadow-2xl p-6 sm:p-8">
            <h2 className="text-2xl font-display font-bold mb-6 text-primary">Send Us a Message</h2>
            
            {status === 'success' && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4" role="alert">
                    <p className="font-bold">Success!</p>
                    <p className="text-sm">Thank you for your inquiry. We will get back to you soon.</p>
                </div>
            )}

            <form onSubmit={handleSubmit} id="contact-form" className="space-y-4">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-muted-foreground mb-1">Full Name</label>
                    <input 
                        type="text" 
                        id="name" 
                        name="name" 
                        required 
                        className="form-input w-full p-2 border border-border rounded-md focus:border-accent focus:ring focus:ring-accent focus:ring-opacity-50"
                    />
                </div>
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
                    <input 
                        type="email" 
                        id="email" 
                        name="email" 
                        required 
                        className="form-input w-full p-2 border border-border rounded-md focus:border-accent focus:ring focus:ring-accent focus:ring-opacity-50"
                    />
                </div>
                <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-muted-foreground mb-1">Phone Number (Optional)</label>
                    <input 
                        type="tel" 
                        id="phone" 
                        name="phone" 
                        className="form-input w-full p-2 border border-border rounded-md focus:border-accent focus:ring focus:ring-accent focus:ring-opacity-50"
                    />
                </div>
                <div>
                    <label htmlFor="message" className="block text-sm font-medium text-muted-foreground mb-1">Your Message / Inquiry</label>
                    <textarea 
                        id="message" 
                        name="message" 
                        rows={4} 
                        required 
                        className="form-textarea w-full p-2 border border-border rounded-md focus:border-accent focus:ring focus:ring-accent focus:ring-opacity-50"
                    ></textarea>
                </div>
                <button type="submit" className="btn-primary w-full">
                    Submit Inquiry
                </button>
            </form>
        </div>
    );
};


const TuitionPointContact: React.FC = () => {
    const { settings } = useSettingsContext();

    // Dynamically add operating hours FAQ if settings exist
    const faqData: FAQItem[] = [
        ...(settings?.contactHours ? [{
            question: "What are your operating hours?",
            answer: settings.contactHours.replace(/\|/g, '\n'),
        }] : []),
        ...baseFaqData,
    ];

    return (
        <div className="bg-accent text-white min-h-screen">
            <SEO
                title="Contact Tuition Point Qatar - Al Wakra Education Center | Get in Touch"
                description="Contact Tuition Point Education Center in Al Wakra, Qatar. Call +974 3015 8401 or visit us at Villa No. 99, Street 201, Zone 90. Email: tuitionwakra@gmail.com. Get directions and schedule a visit."
                keywords="contact tuition Qatar, Al Wakra education center, tuition center phone, visit tuition point, schedule consultation Qatar, Al Wakra address, tutoring center location, get in touch Qatar, education center contact"
            />

            {/* --------------------- Page Header --------------------- */}
            <section className="pt-16 pb-12 md:pt-20 md:pb-16 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 animate-slide-up">Contact Us</h1>
                        <p className="text-lg text-white animate-slide-up">Get in touch with our team for inquiries about our programs or to schedule a visit.</p>
                    </div>
                </div>
            </section>
            
            {/* --------------------- Contact Info & Form Section --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        
                        {/* Column 1: Map and Details */}
                        <div className="animate-slide-up">
                            <div className="bg-white rounded-xl shadow-lg text-foreground mb-8 p-6 sm:p-8 shadow-xl">
                                <h2 className="text-2xl font-display font-bold mb-6">Our Location & Hours</h2>
                                
                                {/* Map Embed */}
                                <div className="mb-8">
                                    <div className="rounded-xl overflow-hidden h-64 shadow-medium border border-border">
                                        {/* Using a placeholder map URL as a real Google Maps embed requires an API key and is blocked by the environment. */}
                                        <iframe 
                                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14457.06730303728!2d51.603306!3d25.176412!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e459032549d44f9%3A0xcf94b595232750e3!2sAl%20Wakra%20Qatar!5e0!3m2!1sen!2sin!4v1696078709040!5m2!1sen!2sin" 
                                            width="100%" 
                                            height="100%" 
                                            style={{ border: 0 }} 
                                            allowFullScreen={false} 
                                            loading="lazy" 
                                            referrerPolicy="no-referrer-when-downgrade"
                                            title="Tuition Point Location Map"
                                        ></iframe>
                                    </div>
                                </div>
                                
                                {/* Address */}
                                {settings?.contactAddress && (
                                  <div className="flex items-start mb-4">
                                      <MapPin className="w-5 h-5 text-accent mr-3 mt-1 flex-shrink-0" />
                                      <div>
                                          <h3 className="font-medium mb-1">Address</h3>
                                          <p className="text-muted-foreground">{settings.contactAddress}</p>
                                      </div>
                                  </div>
                                )}

                                {/* Office Hours */}
                                {settings?.contactHours && (
                                  <div className="flex items-start mb-4">
                                      <Clock className="w-5 h-5 text-accent mr-3 mt-1 flex-shrink-0" />
                                      <div>
                                          <h3 className="font-medium mb-1">Office Hours</h3>
                                          <p className="text-muted-foreground whitespace-pre-line">{settings.contactHours}</p>
                                      </div>
                                  </div>
                                )}
                                
                                <div className="mt-8 pt-8 border-t border-border">
                                    <h2 className="text-xl font-display font-bold mb-4">Get in Touch</h2>
                                    <div className="space-y-4">
                                        {settings?.contactPhone && (
                                          <div className="flex items-start">
                                              <Phone className="w-5 h-5 text-accent mr-3 mt-1 flex-shrink-0" />
                                              <div>
                                                  <h3 className="font-medium mb-1">Phone / WhatsApp</h3>
                                                  <p className="text-muted-foreground">{settings.contactPhone}</p>
                                              </div>
                                          </div>
                                        )}
                                        {settings?.contactEmail && (
                                          <div className="flex items-start">
                                              <Mail className="w-5 h-5 text-accent mr-3 mt-1 flex-shrink-0" />
                                              <div>
                                                  <h3 className="font-medium mb-1">Email</h3>
                                                  <p className="text-muted-foreground">{settings.contactEmail}</p>
                                              </div>
                                          </div>
                                        )}
                                    </div>
                                    
                                   <div>
                           <h3 className="font-medium mb-4">Connect With Us</h3>
                           <div className="flex space-x-4">
                               {/* Ensured bg-cyan-100 is used as requested */}
                               <a href="#" aria-label="Facebook" className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center hover:bg-opacity-20 transition-colors">
                                   <Facebook className="w-5 h-5 text-accent" />
                               </a>
                               <a href="#" aria-label="Instagram" className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center hover:bg-opacity-20 transition-colors">
                                   <Instagram className="w-5 h-5 text-accent" />
                               </a>
                               <a href="#" aria-label="Twitter" className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center hover:bg-opacity-20 transition-colors">
                                   <Twitter className="w-5 h-5 text-accent" />
                               </a>
                           </div>
                       </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Column 2: Contact Form */}
                        <div className="animate-slide-up animation-delay-1">
                            <ContactForm />
                        </div>
                        
                    </div>
                </div>
            </section>
            
            {/* --------------------- FAQ Section --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16 animate-slide-up">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4">Frequently Asked Questions</h2>
                        <p className="text-lg text-white/80">Find answers to common questions about our programs and enrollment process.</p>
                    </div>
                    
                    <div className="max-w-4xl mx-auto">
                        <div className="space-y-6">
                            {faqData.map((item, index) => (
                                <div key={index} className={`bg-white rounded-xl shadow-lg text-foreground p-6 shadow-md animate-slide-up animation-delay-${index + 1}`}>
                                    <h3 className="text-xl font-display font-bold mb-3 text-primary">{item.question}</h3>
                                    <p className="text-muted-foreground">
                                        {item.answer}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
            
            {/* --------------------- CTA Section --------------------- */}
            <section className="py-24 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 animate-slide-up">Ready to Take the Next Step?</h2>
                        <p className="text-lg mb-8 text-white text-opacity-90 animate-slide-up">Enroll today and join our community of successful learners.</p>
                       <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
                                     {/* UPDATE: Applied explicit Tailwind classes for buttons */}
                                     <Link 
                                       to="/enrollment" 
                                       className="bg-white text-accent hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300  sm:w-auto text-lg"
                                     >
                                       Enroll Today
                                     </Link>
                                     
                                   </div>
                    </div>
                </div>
            </section>
            
          
        </div>
    );
};

export default TuitionPointContact;